import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";

export const HeroContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100svh;
  padding: 140px 24px 40px;
  overflow: hidden;
  background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(139, 124, 255, 0.12), transparent),
    #050507;

  @media (max-width: 640px) {
    padding: 120px 18px 40px;
  }
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 48px;
  align-items: center;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 64px;
    text-align: center;
  }
`;

export const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

export const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 8px 16px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  margin-bottom: 26px;
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55); }
  70% { box-shadow: 0 0 0 7px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
`;

export const PulseDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.success};
  animation: ${pulse} 2.2s infinite;
`;

export const Name = styled(motion.h1)`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 700;
  font-size: clamp(2.6rem, 6vw, 4.6rem);
  line-height: 1.04;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;
`;

export const NameWord = styled(motion.span)`
  display: inline-block;
  margin-right: 0.28em;
`;

export const RoleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-family: "Space Grotesk", sans-serif;
  font-weight: 500;
  font-size: clamp(1.15rem, 2.4vw, 1.6rem);
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 18px;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

export const GradientSpan = styled.span`
  background: ${({ theme }) => theme.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 600;
`;

export const TypewriterWrap = styled.span`
  background: ${({ theme }) => theme.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 600;

  & * {
    background: inherit;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

export const Description = styled(motion.p)`
  max-width: 560px;
  margin-top: 24px;
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};

  @media (max-width: 640px) {
    font-size: 15px;
  }
`;

export const CTARow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 36px;
`;

export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 15px 26px;
  border-radius: 100px;
  background: ${({ theme }) => theme.gradient};
  color: #050507;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 34px rgba(139, 124, 255, 0.35);
  }
`;

export const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 15px 26px;
  border-radius: 100px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: ${({ theme }) => theme.text_primary};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background: rgba(139, 124, 255, 0.08);
    transform: translateY(-3px);
  }
`;

export const MetaRow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 40px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_muted};

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

export const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.text_muted};
`;

/* ---------- Right visual: photo ---------- */

export const RightCol = styled.div`
  display: flex;
  justify-content: center;
  perspective: 1400px;

  @media (max-width: 1024px) {
    order: -1;
  }
`;

export const CardStage = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 420px;
`;

export const PhotoGlow = styled.div`
  position: absolute;
  inset: -10%;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradient};
  filter: blur(70px);
  opacity: 0.35;
  z-index: -1;
`;

export const PhotoRing = styled(motion.div)`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  padding: 6px;
  background: ${({ theme }) => theme.gradient};
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  transform-style: preserve-3d;
`;

export const PhotoMask = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: #050507;
`;

export const Photo = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.6);
  transform-origin: 50% 30%;
  mask-image: radial-gradient(circle closest-side at 50% 50%, black 42%, transparent 86%);
  -webkit-mask-image: radial-gradient(circle closest-side at 50% 50%, black 42%, transparent 86%);
`;

export const Chip = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(10, 10, 14, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.4);
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`;

export const ChipDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $c, theme }) => $c || theme.secondary};
`;

/* ---------- Scroll cue ---------- */

const bounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50% { transform: translateY(8px); opacity: 1; }
`;

export const ScrollCue = styled.a`
  position: absolute;
  bottom: 92px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.text_muted};
  text-decoration: none;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  z-index: 1;

  svg {
    animation: ${bounce} 1.8s ease-in-out infinite;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

/* ---------- Marquee ---------- */

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

export const MarqueeWrap = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 64px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, black 12%, black 88%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, black 12%, black 88%, transparent);

  @media (max-width: 640px) {
    margin-top: 40px;
  }
`;

export const MarqueeTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 56px;
  width: max-content;
  animation: ${scroll} 28s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const MarqueeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.text_muted};
  font-family: "Space Grotesk", sans-serif;
  font-weight: 500;
  font-size: 15px;
  white-space: nowrap;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
    filter: grayscale(1) brightness(0) invert(1);
    opacity: 0.55;
  }
`;
